/**
 * The client's capability model: what the signed-in person may do in one
 * workspace, derived from their membership's `role` plus its
 * `capability_overrides` exactly as the server derives it.
 *
 * Only a hint for which controls to enable. The server is the authority: a
 * control enabled here can still be answered `403 FORBIDDEN`, and that answer
 * is always shown. What this module buys is that a person is not offered a
 * button the server can only refuse; such a control is rendered disabled with
 * a translated reason (`capabilityReasonKey`), never hidden silently.
 */
import type { components, paths } from '@cedarreachai/api-client';
import {
  GENERATED_APPROVER_RULE,
  GENERATED_APPROVER_RULES,
  GENERATED_ROUTE_CAPABILITIES,
  type ApproverRule,
} from './routeCapabilities.generated';

type Schemas = components['schemas'];

export type Role = Schemas['Role'];
export type Capability = Schemas['Capability'];
export type Membership = Schemas['Membership'];
export type Session = Schemas['Session'];
export type RiskClass = Schemas['RiskClass'];

/**
 * Mirror of `Role::default_capabilities` in
 * `crates/domain/src/invariants/automation.rs` — same roles, same
 * capabilities, same order. `scripts/check_capability_drift.py` (run by
 * `make contracts`) parses both tables and fails on any difference, so edit
 * the Rust table first and copy it here.
 *
 * `authorize_paid_spend` is in no role (paid-media authority is granted only
 * by an explicit override, design doc §3.3).
 */
export const ROLE_CAPABILITIES: Readonly<Record<Role, readonly Capability[]>> = {
  owner: [
    'manage_workspace',
    'manage_members',
    'manage_brands',
    'manage_connectors',
    'manage_policies',
    'import_sources',
    'propose_facts',
    'verify_facts',
    'manage_campaigns',
    'edit_content',
    'submit_for_approval',
    'approve_content',
    'schedule_content',
    'publish_content',
    'run_agents',
    'view_analytics',
    'manage_experiments',
    'view_audit',
    'manage_costs',
    'export_data',
    'delete_data',
    'change_automation_level',
    'global_pause',
  ],
  admin: [
    'manage_brands',
    'manage_connectors',
    'manage_policies',
    'manage_members',
    'import_sources',
    'propose_facts',
    'verify_facts',
    'manage_campaigns',
    'edit_content',
    'submit_for_approval',
    'schedule_content',
    'publish_content',
    'run_agents',
    'view_analytics',
    'manage_experiments',
    'view_audit',
    'manage_costs',
    'export_data',
    'change_automation_level',
    'global_pause',
  ],
  editor: [
    'import_sources',
    'propose_facts',
    'manage_campaigns',
    'edit_content',
    'submit_for_approval',
    'run_agents',
    'view_analytics',
  ],
  approver: ['approve_content', 'verify_facts', 'schedule_content', 'view_analytics', 'view_audit'],
  analyst: ['view_analytics', 'manage_experiments'],
  viewer: ['view_analytics'],
};

/**
 * The resolved capability set: role defaults plus every override.
 *
 * Overrides only ever grant (`Subject::capabilities` in
 * `crates/application/src/authorization.rs` folds them in with `granting`);
 * there is no override that withdraws a role default.
 */
export function capabilitiesOf(membership: Pick<Membership, 'role' | 'capability_overrides'>) {
  return new Set<Capability>([
    ...(ROLE_CAPABILITIES[membership.role] ?? []),
    ...membership.capability_overrides,
  ]);
}

/**
 * The designated-approver rule the client applies: the contract's
 * `x-approver-rule`, generated into `routeCapabilities.generated.ts` by
 * `scripts/gen_route_capabilities.mjs`. It is the only rule (ADR-031): there
 * is no hand-kept fallback, and the generator exits 2 rather than render a
 * contract whose rule it cannot read, so a missing rule cannot fall through to
 * "anyone holding `approve_content`". `scripts/check_capability_drift.py`
 * compares it with `is_designated_approver` in
 * `crates/application/src/use_cases/approvals.rs` (today: `low` and `medium`
 * need nothing beyond `approve_content`; `high` needs the `approver` role or
 * `manage_policies`; `critical` needs the `owner` role).
 */
export const APPROVER_RULE: ApproverRule = GENERATED_APPROVER_RULE;

/**
 * Whether this membership may decide an approval request of `riskClass`, on
 * top of holding `approve_content` (design doc §10.1), by {@link APPROVER_RULE}.
 *
 * The server checks it after confirming the request is still `pending` and
 * answers `403 FORBIDDEN`; the mock persona gate refuses the same way, and the
 * approval screen disables its decision controls with the reason beforehand.
 */
export function isDesignatedApprover(
  membership: Pick<Membership, 'role' | 'capability_overrides'>,
  riskClass: RiskClass,
): boolean {
  const designation = APPROVER_RULE[riskClass];
  if (!designation) return true;
  if (designation.anyOfRoles.includes(membership.role)) return true;
  const held = capabilitiesOf(membership);
  return designation.anyOfCapabilities.some((capability) => held.has(capability));
}

/** Why a capability is not (yet) held. Each has a translated reason. */
export type CapabilityDenial =
  /** The session is still loading: nothing is offered until the role is known. */
  | 'checking'
  /** The session has no membership in this workspace. */
  | 'not_member'
  /** The membership's role and overrides do not include the capability. */
  | 'missing_capability';

/** The answer to `can(workspaceId, capability)`. */
export type CapabilityAnswer =
  | { readonly allowed: true }
  | { readonly allowed: false; readonly denial: CapabilityDenial; readonly role: Role | null };

/**
 * Whether `session` holds `capability` in `workspaceId`.
 *
 * `undefined` session means "still loading" (`checking`); a session without a
 * membership in the workspace holds nothing (`not_member`).
 */
export function can(
  session: Pick<Session, 'memberships'> | undefined,
  workspaceId: string,
  capability: Capability,
): CapabilityAnswer {
  if (!session) return { allowed: false, denial: 'checking', role: null };
  const membership = session.memberships.find((m) => m.workspace_id === workspaceId);
  if (!membership) return { allowed: false, denial: 'not_member', role: null };
  if (capabilitiesOf(membership).has(capability)) return { allowed: true };
  return { allowed: false, denial: 'missing_capability', role: membership.role };
}

/**
 * The same question for a membership already in hand. `undefined` is
 * "loading", `null` is "no membership".
 */
export function membershipCan(
  membership: Pick<Membership, 'role' | 'capability_overrides'> | null | undefined,
  capability: Capability,
): boolean {
  return membership ? capabilitiesOf(membership).has(capability) : false;
}

type MutatingMethod = 'post' | 'put' | 'patch' | 'delete';

/**
 * Every mutating operation in the generated contract, as `"METHOD /path"`
 * with the contract's own path template. Derived from `paths`, so a new
 * operation in `contracts/openapi.yaml` makes {@link ROUTE_CAPABILITIES}
 * fail to type-check until someone says what it requires.
 */
export type MutatingRoute = {
  [P in keyof paths & string]: {
    [M in MutatingMethod]: [NonNullable<paths[P][M]>] extends [never]
      ? never
      : `${Uppercase<M>} ${P}`;
  }[MutatingMethod];
}[keyof paths & string];

/**
 * What each mutating operation requires in its workspace: the contract's
 * machine-readable `x-required-capabilities`, generated into
 * `routeCapabilities.generated.ts` by `scripts/gen_route_capabilities.mjs`
 * (never hand-kept; `pnpm lint` fails when the generated file is stale and
 * `scripts/check_capability_drift.py` when it disagrees with the contract).
 * **Every** listed capability must be held. An empty list means no workspace
 * capability (a public endpoint, a signed webhook, sign-out, or "any
 * signed-in user").
 *
 * Exhaustive by type ({@link MutatingRoute}): a contract operation the
 * generated table lacks, or one it names that the generated client does not,
 * fails to type-check. The mock backend's persona gate
 * (`src/test/msw/persona.ts`) refuses from this table, so the mock cannot
 * drift from what the client believes a role may call. The server stays the
 * authority. The designated-approver rule depends on the stored request, not
 * the operation: see {@link isDesignatedApprover} and
 * {@link DESIGNATED_APPROVER_ROUTES}, which the persona gate applies after
 * this table.
 */
export const ROUTE_CAPABILITIES: Readonly<Record<MutatingRoute, readonly Capability[]>> =
  GENERATED_ROUTE_CAPABILITIES satisfies Readonly<
    Record<KnownRoute<keyof typeof GENERATED_ROUTE_CAPABILITIES>, readonly Capability[]>
  >;

/** Fails to type-check when the generated table names an operation the client does not know. */
type KnownRoute<K extends MutatingRoute> = K;

/**
 * The operations that decide an approval request, where the server also
 * applies {@link isDesignatedApprover} to the request's risk class: exactly
 * those carrying `x-approver-rule` in the contract (the generator requires it
 * on every mutating operation that requires `approve_content`, and on no
 * other).
 */
export const DESIGNATED_APPROVER_ROUTES: ReadonlySet<MutatingRoute> = new Set<MutatingRoute>(
  Object.keys(GENERATED_APPROVER_RULES) as MutatingRoute[],
);

/** The route templates, compiled once, for matching a concrete request path. */
const ROUTE_MATCHERS: ReadonlyArray<{
  readonly method: string;
  readonly template: string;
  readonly pattern: RegExp;
  readonly capabilities: readonly Capability[];
}> = Object.entries(ROUTE_CAPABILITIES).map(([key, capabilities]) => {
  const space = key.indexOf(' ');
  const template = key.slice(space + 1);
  const source = template
    .split(/(\{[^}]+\})/)
    .map((part) => (part.startsWith('{') ? '([^/]+)' : part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')))
    .join('');
  return {
    method: key.slice(0, space),
    template,
    pattern: new RegExp(`^${source}$`),
    capabilities,
  };
});

/** A concrete request matched to its contract operation. */
export interface RouteRequirement {
  readonly route: MutatingRoute;
  /** The `{workspace_id}` the path names, or `null` for a route outside a workspace. */
  readonly workspaceId: string | null;
  readonly capabilities: readonly Capability[];
  /** Every path parameter by its template name (`approval_id`, …), decoded. */
  readonly params: Readonly<Record<string, string>>;
}

/**
 * The contract operation a mutating request is, with what it requires, or
 * `null` for a read or an unknown path. `pathname` is the URL path only.
 */
export function routeRequirement(method: string, pathname: string): RouteRequirement | null {
  const upper = method.toUpperCase();
  let best: (typeof ROUTE_MATCHERS)[number] | null = null;
  let match: RegExpExecArray | null = null;
  for (const candidate of ROUTE_MATCHERS) {
    if (candidate.method !== upper) continue;
    const found = candidate.pattern.exec(pathname);
    // A literal segment beats a parameter: prefer the template with fewer `{`.
    if (found && (!best || paramCount(candidate.template) < paramCount(best.template))) {
      best = candidate;
      match = found;
    }
  }
  if (!best || !match) return null;
  const names = [...best.template.matchAll(/\{([^}]+)\}/g)].map((m) => m[1] ?? '');
  const groups = match;
  const params: Record<string, string> = {};
  names.forEach((name, i) => {
    params[name] = decodeURIComponent(groups[i + 1] ?? '');
  });
  return {
    route: `${best.method} ${best.template}` as MutatingRoute,
    workspaceId: params['workspace_id'] ?? null,
    capabilities: best.capabilities,
    params,
  };
}

function paramCount(template: string): number {
  return template.split('{').length - 1;
}
